import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Building, 
  Briefcase, 
  Target, 
  Award,
  Clock,
  Edit3,
  Save,
  X,
  Upload,
  Download,
  Plus,
  Trash2
} from 'lucide-react';
import { OutlookAvailabilityPicker } from '@/components/ui/outlook-availability-picker';
import api, { profileAPI } from '@/services/api';
import { resolveProfileImageUrl } from '@/utils/profileImageUrl';
import DiscordLinkCard from '@/components/profile/DiscordLinkCard';

interface MentorProfileData {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  address?: string;
  bio?: string;
  dateOfBirth?: string;
  tShirtSize?: string;
  role: string;
  registrationNumber?: string;
  profileImageUrl?: string;
  primarySkills?: string[];
  secondarySkills?: string[];
  isEmailVerified: boolean;
  isApproved: boolean;
  profileCompleted: number;
  professionalInfo?: {
    currentCompany: string;
    currentPosition: string;
    yearsOfExperience: number;
    industry: string;
    department: string;
  };
  expertise?: {
    technical: string[];
    industries: string[];
    mentoring: string[];
  };
  availability?: Array<{
    _id: string;
    day: string;
    timeSlots: string[];
  }>;
  mentoringApproach?: string;
  achievements?: string[];
  createdAt: string;
  updatedAt: string;
}

const MentorProfile: React.FC = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<MentorProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<MentorProfileData>>({});
  const [signedProfileImageUrl, setSignedProfileImageUrl] = useState<string | null>(null);

  // Fetch profile data
  useEffect(() => {
    fetchProfile();
  }, []);

  const getSignedProfileImageUrl = async (imageUrl: string) => resolveProfileImageUrl(imageUrl);

  // Update signed profile image URL when profile changes
  useEffect(() => {
    if (profile?.profileImageUrl) {
      getSignedProfileImageUrl(profile.profileImageUrl).then(url => {
        setSignedProfileImageUrl(url);
      });
    } else {
      setSignedProfileImageUrl(null);
    }
  }, [profile?.profileImageUrl]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      
      // Try using profileAPI first, fallback to direct api call
      let response;
      if (profileAPI && typeof profileAPI.getProfile === 'function') {
        response = await profileAPI.getProfile();
      } else {
        response = await api.get('/profile');
        response = response.data;
      }
      
      if (response.success && response.data) {
        setProfile(response.data);
        setFormData(response.data);
      } else {
        throw new Error(response.message || 'Failed to fetch profile');
      }
    } catch (err: any) {
      setError('Failed to fetch profile');
      console.error('Profile fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle nested object changes
  const handleNestedChange = (parent: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...(prev[parent as keyof MentorProfileData] as any),
        [field]: value
      }
    }));
  };

  // Handle array field changes
  const handleArrayChange = (parent: string, field: string, value: string[]) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...(prev[parent as keyof MentorProfileData] as any),
        [field]: value
      }
    }));
  };



  // Save profile
  const saveProfile = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      // Debug logging
      console.log('profileAPI:', profileAPI);
      console.log('profileAPI.updateProfile:', profileAPI?.updateProfile);
      console.log('Form data being sent:', formData);
      console.log('Primary skills:', formData.primarySkills);
      console.log('Secondary skills:', formData.secondarySkills);
      console.log('Professional info:', formData.professionalInfo);
      console.log('Expertise:', formData.expertise);
      console.log('Availability:', formData.availability);
      console.log('Mentoring approach:', formData.mentoringApproach);
      console.log('Achievements:', formData.achievements);
      
      let response;
      if (profileAPI && typeof profileAPI.updateProfile === 'function') {
        response = await profileAPI.updateProfile(formData);
      } else {
        // Fallback to direct API call
        console.log('Using fallback API call');
        response = await api.put('/profile', formData);
        response = response.data;
      }
      
      if (response.success && response.data) {
        setProfile(response.data);
        setIsEditing(false);
        setSuccess('Profile updated successfully');
      } else {
        throw new Error(response.message || 'Failed to update profile');
      }
      
    } catch (err: any) {
      console.error('Profile update error:', err);
      
      // Handle validation errors specifically
      if (err.response && err.response.status === 400) {
        const errorData = err.response.data;
        if (errorData.details && Array.isArray(errorData.details)) {
          const validationErrors = errorData.details.map((detail: any) => detail.msg).join(', ');
          setError(`Validation errors: ${validationErrors}`);
        } else {
          setError(errorData.message || 'Failed to update profile');
        }
      } else {
        setError('Failed to update profile');
      }
    } finally {
      setSaving(false);
    }
  };

  // Cancel editing
  const cancelEditing = () => {
    setFormData(profile || {});
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  // Handle file upload (only for profile image)
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await profileAPI.uploadProfileImage(file);

      if (response.success) {
        setSuccess('New profile image uploaded successfully.');
        fetchProfile(); // Refresh profile data
      } else {
        throw new Error(response.message || 'Failed to upload profile image');
      }
    } catch (err: any) {
      if (err.response && err.response.status === 413) {
        setError(err.response.data.message || 'File is too large. Maximum size is 10MB.');
      } else {
        setError('Failed to upload profile image. Please try again.');
      }
      console.error('File upload error:', err);
    } finally {
      setSaving(false);
      // Reset file input to allow re-uploading the same file
      event.target.value = '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center px-4">
        <div className="text-center space-y-6 max-w-md">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto"></div>
            <div className="absolute inset-0 rounded-full border-4 border-blue-200 animate-pulse"></div>
          </div>
          <div className="space-y-3">
            <h3 className="text-2xl font-bold text-secondary">Loading Profile</h3>
            <p className="text-gray-600 leading-relaxed">Preparing your mentoring workspace...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center px-4">
        <div className="text-center space-y-6 max-w-md">
          <div className="p-6 bg-red-50 rounded-2xl w-20 h-20 mx-auto flex items-center justify-center shadow-lg">
            <User className="h-10 w-10 text-red-500" />
          </div>
          <div className="space-y-3">
            <h3 className="text-2xl font-bold text-secondary">Profile Not Found</h3>
            <p className="text-gray-600 leading-relaxed">Unable to load your profile information</p>
          </div>
          <Button onClick={fetchProfile} className="bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <div className="bg-surface shadow-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center space-y-4 lg:space-y-0">
            <div className="space-y-2">
              <h1 className="text-4xl font-bold text-secondary">
                Mentor Profile
              </h1>
              <p className="text-gray-600 text-lg">Share your expertise and guide the next generation</p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
              <Button 
                variant="outline" 
                className="w-full sm:w-auto bg-surface border-gray-300 hover:bg-background px-6 py-3 rounded-xl font-semibold transition-all duration-300"
                onClick={() => navigate('/mentor/dashboard')}
              >
                <Briefcase className="w-5 h-5 mr-2" />
                Dashboard
              </Button>
              {!isEditing ? (
                <Button 
                  onClick={() => setIsEditing(true)}
                  className="w-full sm:w-auto bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <Edit3 className="w-5 h-5 mr-2" />
                  Edit Profile
                </Button>
              ) : (
                <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                  <Button 
                    onClick={cancelEditing}
                    variant="outline"
                    className="w-full sm:w-auto bg-surface border-gray-300 hover:bg-background px-6 py-3 rounded-xl font-semibold transition-all duration-300"
                  >
                    <X className="w-5 h-5 mr-2" />
                    Cancel
                  </Button>
                  <Button 
                    onClick={saveProfile}
                    disabled={saving}
                    className="w-full sm:w-auto bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    {saving ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    ) : (
                      <Save className="w-5 h-5 mr-2" />
                    )}
                    Save Changes
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Success/Error Messages */}
        {success && (
          <div className="mb-8 bg-green-50 border border-green-200 text-green-700 px-6 py-4 rounded-xl shadow-sm">
            <div className="flex items-center">
              <div className="w-5 h-5 bg-primary rounded-full mr-3"></div>
              {success}
            </div>
          </div>
        )}
        {error && (
          <div className="mb-8 bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl shadow-sm">
            <div className="flex items-center">
              <div className="w-5 h-5 bg-red-500 rounded-full mr-3"></div>
              {error}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left Column - Profile Info & Stats */}
          <div className="lg:col-span-1 space-y-8">
            {/* Profile Photo */}
            <Card className="border-border shadow-lg rounded-2xl overflow-hidden">
              <CardContent className="p-8">
                <div className="text-center space-y-6">
                  <div className="relative w-48 h-48 mx-auto group">
                    <label htmlFor="profile-image-upload" className="cursor-pointer">
                      <div className="w-48 h-48 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white text-6xl font-bold transition-all duration-300 group-hover:brightness-90">
                        {signedProfileImageUrl ? (
                          <img
                            src={signedProfileImageUrl}
                            alt="Profile"
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          <User size={80} />
                        )}
                      </div>
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 rounded-full flex items-center justify-center transition-all duration-300">
                        <Edit3 className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all duration-300" />
                      </div>
                    </label>
                    <input
                      type="file"
                      id="profile-image-upload"
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => handleFileUpload(e)}
                    />
                  </div>
                  <div className="space-y-3">
                    <h2 className="text-3xl font-bold text-secondary">
                      {profile.firstName} {profile.lastName}
                    </h2>
                    <Badge className="bg-primary-light text-blue-800 px-4 py-2 text-sm font-semibold rounded-full">
                      Professional Mentor
                    </Badge>
                    {profile.registrationNumber && (
                      <div className="flex items-center justify-center space-x-2 p-3 bg-background rounded-lg">
                        <span className="text-sm font-semibold text-gray-600">Registration #:</span>
                        <span className="text-lg font-bold text-primary font-mono">{profile.registrationNumber}</span>
                      </div>
                    )}
                    <p className="text-gray-600 text-lg">Sharing knowledge and experience</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-900">Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-slate-400" />
                  <span className="text-slate-700">{profile.email}</span>
                </div>
                {profile.phone && (
                  <div className="flex items-center space-x-3">
                    <Phone className="w-5 h-5 text-slate-400" />
                    <span className="text-slate-700">{profile.phone}</span>
                  </div>
                )}
                {profile.address && (
                  <div className="flex items-center space-x-3">
                    <MapPin className="w-5 h-5 text-slate-400" />
                    <span className="text-slate-700">{profile.address}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Account Status */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-900">Account Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Email Verified</span>
                  <Badge variant={profile.isEmailVerified ? "default" : "secondary"}>
                    {profile.isEmailVerified ? "✓ Verified" : "⚠ Pending"}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Account Status</span>
                  <Badge variant={profile.isApproved ? "default" : "secondary"}>
                    {profile.isApproved ? "✓ Active" : "⚠ Pending Approval"}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Profile Complete</span>
                  <span className="text-2xl font-bold text-primary">{profile.profileCompleted}%</span>
                </div>
              </CardContent>
            </Card>

            <DiscordLinkCard />

          </div>

          {/* Right Column - Profile Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-900">Basic Information</CardTitle>
                <CardDescription>Your personal and contact details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="text-sm font-medium text-slate-700">
                      First Name
                    </Label>
                    {isEditing ? (
                      <Input
                        id="firstName"
                        value={formData.firstName || ''}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        className="border-slate-200 focus:border-primary focus:ring-green-500"
                      />
                    ) : (
                      <p className="text-slate-900 font-medium">{profile.firstName}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="text-sm font-medium text-slate-700">
                      Last Name
                    </Label>
                    {isEditing ? (
                      <Input
                        id="lastName"
                        value={formData.lastName || ''}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        className="border-slate-200 focus:border-primary focus:ring-green-500"
                      />
                    ) : (
                      <p className="text-slate-900 font-medium">{profile.lastName}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-sm font-medium text-slate-700">
                      Phone Number
                    </Label>
                    {isEditing ? (
                      <Input
                        id="phone"
                        value={formData.phone || ''}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className="border-slate-200 focus:border-primary focus:ring-green-500"
                      />
                    ) : (
                      <p className="text-slate-900 font-medium">{profile.phone || 'Not provided'}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="address" className="text-sm font-medium text-slate-700">
                      Address
                    </Label>
                    {isEditing ? (
                      <Input
                        id="address"
                        value={formData.address || ''}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        className="border-slate-200 focus:border-primary focus:ring-green-500"
                      />
                    ) : (
                      <p className="text-slate-900 font-medium">{profile.address || 'Not provided'}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="dateOfBirth" className="text-sm font-medium text-slate-700">
                      Date of Birth (Optional)
                    </Label>
                    {isEditing ? (
                      <Input
                        id="dateOfBirth"
                        type="date"
                        value={formData.dateOfBirth ? new Date(formData.dateOfBirth).toISOString().split('T')[0] : ''}
                        onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                        className="border-slate-200 focus:border-primary focus:ring-green-500"
                      />
                    ) : (
                      <p className="text-slate-900 font-medium">
                        {profile.dateOfBirth ? new Date(profile.dateOfBirth).toLocaleDateString() : 'Not provided'}
                      </p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="tShirtSize" className="text-sm font-medium text-slate-700">
                      T-Shirt Size (Optional)
                    </Label>
                    {isEditing ? (
                      <Select 
                        value={formData.tShirtSize || ''} 
                        onValueChange={(value) => handleInputChange('tShirtSize', value)}
                      >
                        <SelectTrigger className="border-slate-200 focus:border-primary focus:ring-green-500">
                          <SelectValue placeholder="Select size" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="XS">XS</SelectItem>
                          <SelectItem value="S">S</SelectItem>
                          <SelectItem value="M">M</SelectItem>
                          <SelectItem value="L">L</SelectItem>
                          <SelectItem value="XL">XL</SelectItem>
                          <SelectItem value="XXL">XXL</SelectItem>
                          <SelectItem value="XXXL">XXXL</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-slate-900 font-medium">{profile.tShirtSize || 'Not provided'}</p>
                    )}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="bio" className="text-sm font-medium text-slate-700">
                    Bio
                  </Label>
                  {isEditing ? (
                    <Textarea
                      id="bio"
                      value={formData.bio || ''}
                      onChange={(e) => handleInputChange('bio', e.target.value)}
                      placeholder="Tell us about yourself and your mentoring approach..."
                      className="border-slate-200 focus:border-primary focus:ring-green-500"
                      rows={3}
                    />
                  ) : (
                    <p className="text-slate-700">{profile.bio || 'No bio provided'}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Professional Information */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
                  <Building className="w-5 h-5 mr-2 text-primary" />
                  Professional Information
                </CardTitle>
                <CardDescription>Your current role and experience</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="currentCompany" className="text-sm font-medium text-slate-700">
                      Current Company
                    </Label>
                    {isEditing ? (
                      <Input
                        id="currentCompany"
                        value={formData.professionalInfo?.currentCompany || ''}
                        onChange={(e) => handleNestedChange('professionalInfo', 'currentCompany', e.target.value)}
                        placeholder="e.g., TechCorp Solutions"
                        className="border-slate-200 focus:border-primary focus:ring-green-500"
                      />
                    ) : (
                      <p className="text-slate-900 font-medium">{profile.professionalInfo?.currentCompany || 'Not specified'}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="currentPosition" className="text-sm font-medium text-slate-700">
                      Current Position
                    </Label>
                    {isEditing ? (
                      <Input
                        id="currentPosition"
                        value={formData.professionalInfo?.currentPosition || ''}
                        onChange={(e) => handleNestedChange('professionalInfo', 'currentPosition', e.target.value)}
                        placeholder="e.g., Senior Software Engineer"
                        className="border-slate-200 focus:border-primary focus:ring-green-500"
                      />
                    ) : (
                      <p className="text-slate-900 font-medium">{profile.professionalInfo?.currentPosition || 'Not specified'}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="yearsOfExperience" className="text-sm font-medium text-slate-700">
                      Years of Experience
                    </Label>
                    {isEditing ? (
                      <Input
                        id="yearsOfExperience"
                        type="number"
                        value={formData.professionalInfo?.yearsOfExperience || ''}
                        onChange={(e) => handleNestedChange('professionalInfo', 'yearsOfExperience', parseInt(e.target.value))}
                        placeholder="e.g., 8"
                        className="border-slate-200 focus:border-primary focus:ring-green-500"
                      />
                    ) : (
                      <p className="text-slate-900 font-medium">{profile.professionalInfo?.yearsOfExperience || 'Not specified'} years</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="industry" className="text-sm font-medium text-slate-700">
                      Industry
                    </Label>
                    {isEditing ? (
                      <Input
                        id="industry"
                        value={formData.professionalInfo?.industry || ''}
                        onChange={(e) => handleNestedChange('professionalInfo', 'industry', e.target.value)}
                        placeholder="e.g., Technology"
                        className="border-slate-200 focus:border-primary focus:ring-green-500"
                      />
                    ) : (
                      <p className="text-slate-900 font-medium">{profile.professionalInfo?.industry || 'Not specified'}</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="department" className="text-sm font-medium text-slate-700">
                      Department
                    </Label>
                    {isEditing ? (
                      <Input
                        id="department"
                        value={formData.professionalInfo?.department || ''}
                        onChange={(e) => handleNestedChange('professionalInfo', 'department', e.target.value)}
                        placeholder="e.g., Engineering"
                        className="border-slate-200 focus:border-primary focus:ring-green-500"
                      />
                    ) : (
                      <p className="text-slate-900 font-medium">{profile.professionalInfo?.department || 'Not specified'}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Expertise */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
                  <Award className="w-5 h-5 mr-2 text-primary" />
                  Expertise & Skills
                </CardTitle>
                <CardDescription>Your technical and mentoring expertise</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Primary Skills</Label>
                    {isEditing ? (
                      <div className="space-y-2">
                        <Textarea
                          value={typeof formData.primarySkills === 'string' ? formData.primarySkills : formData.primarySkills?.join(', ') || ''}
                          onChange={(e) => {
                            if (e.target.value.length <= 2000) {
                              handleInputChange('primarySkills', e.target.value);
                            }
                          }}
                          onBlur={(e) => {
                            const skills = e.target.value.split(',').map(s => s.trim()).filter(s => s);
                            setFormData(prev => ({
                              ...prev,
                              primarySkills: skills
                            }));
                          }}
                          placeholder="e.g., Java, React, Financial Reporting, Accounting, Data Analysis, etc."
                          className="border-slate-200 focus:border-primary focus:ring-green-500 min-h-[80px]"
                          maxLength={2000}
                        />
                        <p className="text-xs text-gray-500">
                          {(typeof formData.primarySkills === 'string' ? formData.primarySkills : formData.primarySkills?.join(', ') || '').length}/2000 characters
                        </p>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {profile.primarySkills?.map((skill, index) => (
                          <Badge key={index} variant="secondary">{skill}</Badge>
                        )) || <p className="text-slate-500">No primary skills listed</p>}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Secondary Skills</Label>
                    {isEditing ? (
                      <div className="space-y-2">
                        <Textarea
                          value={typeof formData.secondarySkills === 'string' ? formData.secondarySkills : formData.secondarySkills?.join(', ') || ''}
                          onChange={(e) => {
                            if (e.target.value.length <= 2000) {
                              handleInputChange('secondarySkills', e.target.value);
                            }
                          }}
                          onBlur={(e) => {
                            const skills = e.target.value.split(',').map(s => s.trim()).filter(s => s);
                            setFormData(prev => ({
                              ...prev,
                              secondarySkills: skills
                            }));
                          }}
                          placeholder="e.g., Python, Node.js, Team Leadership, Project Management, etc."
                          className="border-slate-200 focus:border-primary focus:ring-green-500 min-h-[80px]"
                          maxLength={2000}
                        />
                        <p className="text-xs text-gray-500">
                          {(typeof formData.secondarySkills === 'string' ? formData.secondarySkills : formData.secondarySkills?.join(', ') || '').length}/2000 characters
                        </p>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {profile.secondarySkills?.map((skill, index) => (
                          <Badge key={index} variant="outline">{skill}</Badge>
                        )) || <p className="text-slate-500">No secondary skills listed</p>}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Industries (Separate multiple industry areas with commas)</Label>
                    {isEditing ? (
                      <div className="space-y-2">
                        <Textarea
                          value={formData.expertise?.industries?.join(', ') || ''}
                          onChange={(e) => {
                            // Allow any characters including spaces, limit to 2000 characters
                            if (e.target.value.length <= 2000) {
                              const industries = e.target.value === '' ? [] : [e.target.value];
                              handleArrayChange('expertise', 'industries', industries);
                            }
                          }}
                          placeholder="e.g., Technology, E-commerce, FinTech, Healthcare, Education, etc."
                          className="border-slate-200 focus:border-primary focus:ring-green-500 min-h-[80px]"
                          maxLength={2000}
                        />
                        <p className="text-xs text-gray-500">
                          {(formData.expertise?.industries?.join(', ') || '').length}/2000 characters
                        </p>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {profile.expertise?.industries?.map((industry, index) => (
                          <Badge key={index} variant="outline">{industry}</Badge>
                        )) || <p className="text-slate-500">No industries listed</p>}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Mentoring Focus (Separate multiple focus areas with commas)</Label>
                    {isEditing ? (
                      <div className="space-y-2">
                        <Textarea
                          value={formData.expertise?.mentoring?.join(', ') || ''}
                          onChange={(e) => {
                            // Allow any characters including spaces, limit to 2000 characters
                            if (e.target.value.length <= 2000) {
                              const mentoring = e.target.value === '' ? [] : [e.target.value];
                              handleArrayChange('expertise', 'mentoring', mentoring);
                            }
                          }}
                          placeholder="e.g., Career Guidance, Technical Skills, Leadership, Personal Development, etc."
                          className="border-slate-200 focus:border-primary focus:ring-green-500 min-h-[80px]"
                          maxLength={2000}
                        />
                        <p className="text-xs text-gray-500">
                          {(formData.expertise?.mentoring?.join(', ') || '').length}/2000 characters
                        </p>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {profile.expertise?.mentoring?.map((focus, index) => (
                          <Badge key={index} variant="default">{focus}</Badge>
                        )) || <p className="text-slate-500">No mentoring focus listed</p>}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Mentoring Approach */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
                  <Target className="w-5 h-5 mr-2 text-primary" />
                  Mentoring Approach
                </CardTitle>
                <CardDescription>How you approach mentoring and guidance</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Label htmlFor="mentoringApproach" className="text-sm font-medium text-slate-700">
                    Your Mentoring Philosophy
                  </Label>
                  {isEditing ? (
                    <Textarea
                      id="mentoringApproach"
                      value={formData.mentoringApproach || ''}
                      onChange={(e) => handleInputChange('mentoringApproach', e.target.value)}
                      placeholder="Describe your mentoring approach, teaching style, and what mentees can expect..."
                      className="border-slate-200 focus:border-primary focus:ring-green-500"
                      rows={4}
                    />
                  ) : (
                    <p className="text-slate-700">{profile.mentoringApproach || 'No mentoring approach described'}</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Availability */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
                  <Clock className="w-5 h-5 mr-2 text-primary" />
                  Availability
                </CardTitle>
                <CardDescription>Your available time slots for mentoring sessions</CardDescription>
              </CardHeader>
              <CardContent>
                <OutlookAvailabilityPicker
                  availability={formData.availability || []}
                  onAvailabilityChange={(newAvailability) => handleInputChange('availability', newAvailability)}
                  isEditing={isEditing}
                />
              </CardContent>
            </Card>

            {/* Achievements */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-900 flex items-center">
                  <Award className="w-5 h-5 mr-2 text-primary" />
                  Achievements & Recognition
                </CardTitle>
                <CardDescription>Your professional achievements and accolades</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Key Achievements</Label>
                  {isEditing ? (
                    <Textarea
                      value={formData.achievements?.join('\n') || ''}
                      onChange={(e) => handleInputChange('achievements', e.target.value.split('\n').filter(s => s.trim()))}
                      placeholder="List your key achievements, one per line..."
                      className="border-slate-200 focus:border-primary focus:ring-green-500"
                      rows={4}
                    />
                  ) : (
                    <div className="space-y-2">
                      {profile.achievements?.map((achievement, index) => (
                        <div key={index} className="flex items-start space-x-2">
                          <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                          <p className="text-slate-700">{achievement}</p>
                        </div>
                      )) || <p className="text-slate-500">No achievements listed</p>}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MentorProfile; 